import { AppLayout } from '@/Layouts';
import { useLocalizedRoute } from '@/hooks';
import React, { useEffect, useMemo, useRef } from 'react';
import { PageProps, PaginatedResource, Recipe, SearchRequest } from '@/types';
import { FoodFilter, RecipeCard, TagFilter } from '@/Components/Specific';
import { UI } from '@/Components/UI';
import { useInertiaForm } from 'use-inertia-form';
import { useTranslation } from 'react-i18next';
import { Head, WhenVisible } from '@inertiajs/react';
import { isEmpty } from '@/utils';

type SearchPageProps = PageProps<{
    request: SearchRequest,
    results: PaginatedResource<Recipe>
}>

export default function({ request, results }: SearchPageProps) {
    const { t } = useTranslation(['search']);
    const { localizedRoute } = useLocalizedRoute();

    const { data, setData, get, setDefaults, reset } = useInertiaForm<SearchRequest>(request);
    const dataRef = useRef(data);

    const isDirty = useMemo(() => {
        const dirtyValues = Object
            .values(data)
            .filter((v) => !isEmpty(v));

        return dirtyValues.length !== 0;
    }, [data]);

    useEffect(() => {
        setDefaults({
            q: undefined,
            food: undefined,
            tags: undefined,
            difficulty: undefined,
            time: undefined,
        });
    }, []);

    // Automatically submit the form when the search query changes (debounced)
    useEffect(() => {
        const handler = setTimeout(() => {
            const prevData = dataRef.current;

            if (JSON.stringify(prevData) !== JSON.stringify(data)) {
                submit();
                dataRef.current = data;
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [data.q]);

    // Automatically submit the form when the filters change
    useEffect(() => {
        const prevData = dataRef.current;

        const { q: _, ...restData } = data;
        const { q: __, ...prevRestData } = prevData;

        if (JSON.stringify(prevRestData) !== JSON.stringify(restData)) {
            submit();
            dataRef.current = data;
        }
    }, [data.food, data.tags, data.difficulty, data.time]);


    const submit = (e?: React.FormEvent) => {
        e?.preventDefault();

        get(localizedRoute('search.index'), {
            async: false,
            preserveState: true,
            replace: true,
            only: ['results']
        });
    };

    return (
        <AppLayout activeItem={'search'}>
            <Head>
                <title>{t('title')}</title>
                <meta name={'description'} content={t('description')} />
                <meta name={'robots'} content={isDirty ? 'noindex,follow' : 'index,follow'} />
                <link rel={'canonical'} href={localizedRoute('search.index')} />
            </Head>

            <UI.Heading
                level={1}
                className={'mt-0'}>
                {t('title')}
            </UI.Heading>

            <div className={'mb-4'}>
                <form onSubmit={submit}>
                    <UI.Form.SearchInput
                        value={data.q}
                        onChange={(search) => setData('q', search)} />
                </form>

                <div className={'flex flex-row gap-2'}>
                    {(data.difficulty || data.food || data.tags || data.time) && (
                        <UI.Button
                            buttonStyle={'tonal'}
                            className={'py-1'}
                            onClick={() => reset(['difficulty', 'food', 'tags', 'time'])}>
                            <UI.Icon name={'filter_alt_off'} />&nbsp;{t('filters.clear')}
                        </UI.Button>
                    )}

                    <FoodFilter
                        value={data.food}
                        onChange={(food: string[]) => setData('food', food)} />

                    <TagFilter
                        value={data.tags}
                        onChange={(tags: string[]) => setData('tags', tags)} />
                </div>
            </div>

            <div className={'grid grid-cols-2 gap-3 md:grid-cols-3'}>
                {results?.data.map((recipe) => (
                    <RecipeCard
                        key={recipe.slug}
                        recipe={recipe} />
                ))}



                {results.meta!.current_page < results.meta!.last_page && (
                    <WhenVisible
                        always={true}
                        buffer={500}
                        fallback={<></>}
                        params={{
                            data: {
                                page: results.meta!.current_page + 1,
                            },
                            only: ['results'],
                            preserveUrl: true,
                        }}>
                        <></>
                    </WhenVisible>
                )}
            </div>
        </AppLayout>
    );
}
